import { useTranslation } from "react-i18next"

import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { useInitials } from "@/hooks/use-initials"
import type { RecentKudo } from "@/types/crew"

/**
 * OKS-396 — Reusable display card for a single kudo (UX Spec §2.B).
 *
 * Layout: sender portrait (initials fallback — no avatar pipeline in V1)
 * · sender name · relative-or-short date, with the verbatim message in
 * full below (no truncation; wraps + breaks long single tokens).
 *
 * Date format (UX Spec §11): relative for the last 7 days
 * ("2 days ago" / "il y a 2 jours" via `Intl.RelativeTimeFormat`,
 * locale-aware), short absolute beyond that ("12 Jun" / "12 juin" via
 * `toLocaleDateString`). Both derive from the BE ISO-8601 `created_at`.
 *
 * Custom component (NOT under `/ui`) — reused by `RecentKudosSection`
 * and any future kudo surface.
 *
 * Locked testid: `kudo-card` (one per rendered kudo).
 */
interface KudoCardProps {
  kudo: RecentKudo
}

export function KudoCard({ kudo }: KudoCardProps) {
  const { i18n } = useTranslation()
  const initials = useInitials()
  const locale = i18n.language || "fr"

  const sentDate = new Date(kudo.created_at)
  const dateLabel = formatKudoDate(sentDate, locale)

  return (
    <article
      data-testid="kudo-card"
      className="flex flex-col gap-2 rounded-md border p-3"
    >
      <div className="flex items-center gap-2">
        <Avatar className="size-8 shrink-0">
          <AvatarFallback className="text-xs">
            {initials(kudo.sender.name)}
          </AvatarFallback>
        </Avatar>
        <div className="flex min-w-0 flex-1 items-baseline justify-between gap-2">
          <span className="truncate text-sm font-medium">
            {kudo.sender.name}
          </span>
          <time
            dateTime={kudo.created_at}
            className="text-muted-foreground shrink-0 text-xs"
          >
            {dateLabel}
          </time>
        </div>
      </div>
      {/* Verbatim message — no truncation (PRD §6 data integrity).
          `break-words` guards a long single-token message from overflow. */}
      <p className="text-sm break-words whitespace-pre-line">{kudo.message}</p>
    </article>
  )
}

/**
 * Relative for the last 7 days, short absolute beyond. Locale-aware via
 * the `Intl` APIs (no translation keys — these are formatting, not copy).
 */
function formatKudoDate(date: Date, locale: string): string {
  const ms = Date.now() - date.getTime()
  const days = Math.floor(ms / (1000 * 60 * 60 * 24))

  if (days < 7) {
    // Not formatRelativeIntl: day-only (floored, future → today) and absolute from day 7 — the helper would say « il y a 3 heures » / « il y a 10 jours ».
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" })
    return rtf.format(-Math.max(0, days), "day")
  }

  return date.toLocaleDateString(locale, { day: "numeric", month: "short" })
}
